// src/components/resources/tools/generators/ReviewMockup/YelpReview.tsx
'use client'

import { Star, UserRound, ThumbsUp } from 'lucide-react'
import Image from 'next/image'
import type { ReviewConfig } from './types'

/**
 * Yelp review mockup component.
 */
export function YelpReview({ config }: { config: ReviewConfig }): JSX.Element {
  return (
    <div className="mx-auto w-full max-w-2xl rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded border-4 border-red-500 bg-gradient-to-br from-orange-400 to-red-500">
            {config.avatar ? (
              <Image
                src={config.avatar}
                alt={`${config.reviewerName}'s avatar`}
                className="h-full w-full object-cover"
                width={56}
                height={56}
                unoptimized
                priority={false}
              />
            ) : (
              <UserRound className="h-6 w-6 text-white" aria-label="User avatar" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-base font-semibold text-gray-900">{config.reviewerName}</span>
              {config.includeBadge && (
                <span className="inline-flex items-center rounded bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800">
                  Elite &#39;25
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="flex items-center gap-1">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-orange-500"></span>
                <span className="font-semibold">142</span> reviews
              </span>
              <span>&middot;</span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-400"></span>
                <span className="font-semibold">89</span> photos
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{config.location}</p>
          </div>
        </div>

        {/* Rating and Date */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-0.5">
            {Array.from({ length: 5 }, (_, i) => (
              <Star
                key={i}
                className={`h-5 w-5 ${i < config.rating ? 'fill-red-500 text-red-500' : 'fill-gray-300 text-gray-300'}`}
              />
            ))}
          </div>
          <span className="text-sm text-gray-500">{config.date}</span>
        </div>

        {/* Review Text */}
        <p className="text-sm text-gray-900 leading-relaxed mb-3">
          {config.reviewText}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-4 pt-3 border-t border-gray-200">
          <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
            <ThumbsUp className="h-4 w-4" />
            <span>Useful</span>
            <span className="font-semibold">8</span>
          </button>
          <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
            <span role="img" aria-label="funny">😂</span>
            <span>Funny</span>
            <span className="font-semibold">2</span>
          </button>
          <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
            <span role="img" aria-label="cool">😍</span>
            <span>Cool</span>
            <span className="font-semibold">5</span>
          </button>
        </div>
      </div>

      {/* Business Response */}
      <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-blue-600 text-white text-sm font-semibold">
            AS
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-gray-900">{config.businessName}</span>
              <span className="text-xs text-gray-500">Business Owner</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mb-1">
              Thank you so much for taking the time to share your experience! We&apos;re excited to continue supporting your team&apos;s success.
            </p>
            <span className="text-xs text-gray-500">3/15/2025</span>
          </div>
        </div>
      </div>
    </div>
  )
}
